import * as React from 'react';

import Cookies from 'js-cookie';

import {
  AUTH_COOKIE_DOMAIN,
  PRIVATE_API_URL,
  REFRESH_TOKEN_ABSOLUTE_DAYS,
} from '@itp/talent/constants/config';

const ACCESS_TOKEN_KEY = 'local_auth_token';
const AUTH_CHANNEL_KEY = 'authChannel';
const REFRESH_TOKEN_KEY = 'local_refresh_token';

const AUTH_COOKIE_NAME = 'tp_auth';

type AuthChannel = 'email' | 'github' | 'google' | 'magic';

interface LocalAuthContextValues {
  authChannel: AuthChannel | null;
  isAuthenticated: boolean;
  logout: () => void;
  refreshAccessToken: () => Promise<null | string>;
  setTokens(token: string, refreshToken: string, authChannel?: AuthChannel): void;
  token: null | string;
}

const LocalAuthContext = React.createContext<LocalAuthContextValues>({
  authChannel: null,
  isAuthenticated: false,
  logout: () => {},
  refreshAccessToken: () => Promise.resolve(null),
  setTokens: () => {},
  token: null,
});

let refreshPromise: null | Promise<null | string> = null;
let currentAccessToken: null | string =
  typeof window !== 'undefined' ? localStorage.getItem(ACCESS_TOKEN_KEY) : null;
let refreshFnRef: (() => Promise<null | string>) | null = null;

export function getLocalToken(): null | string {
  return currentAccessToken;
}

function decodeBase64Url(str: string): string {
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4;
  if (pad === 2) base64 += '==';
  else if (pad === 3) base64 += '=';
  return atob(base64);
}

export function isTokenExpired(token: string, bufferSeconds = 60): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payload = JSON.parse(decodeBase64Url(parts[1]));
    if (!payload.exp) return false;
    return payload.exp * 1000 < Date.now() + bufferSeconds * 1000;
  } catch {
    return true;
  }
}

export async function ensureValidToken(): Promise<null | string> {
  if (refreshPromise) return refreshPromise;

  const token = currentAccessToken;
  if (!token) return null;
  if (!isTokenExpired(token)) return token;

  if (refreshFnRef) return refreshFnRef();
  return null;
}

function getCookieOptions(): Cookies.CookieAttributes {
  const options: Cookies.CookieAttributes = {
    expires: REFRESH_TOKEN_ABSOLUTE_DAYS,
    path: '/',
    sameSite: 'lax',
    secure: true,
  };
  if (AUTH_COOKIE_DOMAIN) {
    options.domain = AUTH_COOKIE_DOMAIN;
  }
  return options;
}

export function setAuthCookie(): void {
  Cookies.set(AUTH_COOKIE_NAME, '1', getCookieOptions());
}

export function removeAuthCookie(): void {
  const removeOptions: Cookies.CookieAttributes = { path: '/' };
  if (AUTH_COOKIE_DOMAIN) removeOptions.domain = AUTH_COOKIE_DOMAIN;
  Cookies.remove(AUTH_COOKIE_NAME, removeOptions);
}

export const LocalAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setTokenState] = React.useState<null | string>(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  });

  const [authChannel, setAuthChannel] = React.useState<AuthChannel | null>(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(AUTH_CHANNEL_KEY) as AuthChannel | null;
  });

  const setTokens = React.useCallback(
    (accessToken: string, refreshToken: string, channel?: AuthChannel) => {
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      if (channel) {
        localStorage.setItem(AUTH_CHANNEL_KEY, channel);
        setAuthChannel(channel);
      }
      currentAccessToken = accessToken;
      setTokenState(accessToken);

      setAuthCookie();

      window.dispatchEvent(
        new CustomEvent('local-token-updated', { detail: { token: accessToken } }),
      );
    },
    [],
  );

  const logout = React.useCallback(() => {
    const rt = localStorage.getItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(AUTH_CHANNEL_KEY);
    currentAccessToken = null;
    setTokenState(null);
    setAuthChannel(null);

    removeAuthCookie();

    if (rt) {
      fetch(`${PRIVATE_API_URL}/auth/refresh/revoke`, {
        body: JSON.stringify({ refreshToken: rt }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      }).catch(() => {});
    }
  }, []);

  const refreshAccessToken = React.useCallback(async (): Promise<null | string> => {
    if (refreshPromise) return refreshPromise;

    const rt = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!rt) {
      logout();
      return null;
    }

    refreshPromise = fetch(`${PRIVATE_API_URL}/auth/refresh`, {
      body: JSON.stringify({ refreshToken: rt }),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    })
      .then(async (res) => {
        if (!res.ok) {
          logout();
          return null;
        }
        const data = await res.json();
        if (data.token && data.refreshToken) {
          setTokens(data.token, data.refreshToken);
          return data.token as string;
        }
        logout();
        return null;
      })
      .catch(() => {
        logout();
        return null;
      })
      .finally(() => {
        refreshPromise = null;
      });

    return refreshPromise;
  }, [logout, setTokens]);

  React.useEffect(() => {
    refreshFnRef = refreshAccessToken;
    return () => {
      refreshFnRef = null;
    };
  }, [refreshAccessToken]);

  const value = React.useMemo(
    () => ({ authChannel, isAuthenticated: !!token, logout, refreshAccessToken, setTokens, token }),
    [authChannel, token, logout, refreshAccessToken, setTokens],
  );

  return <LocalAuthContext.Provider value={value}>{children}</LocalAuthContext.Provider>;
};

export const useLocalAuth = () => React.useContext(LocalAuthContext);
